/**
 * Copyright (c) S2 Partnership Ltd. 2021-2024
 *
 * A service that offers common functionality used by a variety of methods and other services
 * Keys are defined for key data models.
 *
 * @summary UtilityService
 * @author s2
 *
 * Created at     : 17-06-2021
 */

import { Injectable, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Property, ModuleLevelEntry, RegisterStructure } from '../../../modules/app/view-models/smart-sync';
import { ModalController, Platform } from '@ionic/angular';
import { Directory, Filesystem, StatResult } from '@capacitor/filesystem';
import { translationObject } from 'src/constants';
import { DeviceData, NewAction, NewRegisterField, NewRegisterRow } from '../view-models/device-data';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { HtmlModalComponent } from '../../common-ui/components/html-modal/html-modal.component';
import { lastValueFrom, Subject } from 'rxjs';
import { LanguageSelected } from '../view-models/login_data';
import { StorageKey, StorageService } from './storage.service';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

@Injectable()
export class UtilityService {
  /** The event emitter that calls smart sync initialisation when the settings modal is dismissed. */
  @Output() settingsDismissedEvent = new Subject();

  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    public file: File,
    private translateService: TranslateService,
    private platform: Platform,
    private modalController: ModalController,
    private storageService: StorageService
    /* eslint-enable */
  ) {}

  /**
   * Get translations.
   *
   * @arg {string} keys Array of strings to map to.
   * @arg {any} interpolateParams Parameters needed by the translate service.
   * @return {any} Translation key.
   */
  async getTranslations(
    keys: string[],
    interpolateParams: any = {}
  ): Promise<string[]> {
    const translations = await lastValueFrom(this.translateService.get(keys, interpolateParams));
    return Object.keys(translations)
      .map((key: string) => {
        return translations[key];
      });
  }

  /**
   * Generate random ID.
   *
   * @arg {any} array Array of Items that already have an ID (makes sure these aren't reused).
   * @arg {number} min The minimum value that the ID can be.
   * @arg {number} max The maximum value that the ID can be.
   * @return {number} Generated random ID.
   */
  genRandomId(array: any[], min: number = 100000, max: number = 1000000): number {
    while (true) {
      const randId = Math.floor(Math.random() * (max - min) + min);

      if (!array || !array.length) {
        return randId;
      }

      if (array.findIndex(item => item.id === randId) === -1) {
        return randId;
      }
    }
  }

  /**
   * Separates out the time stamp from the file URI and uses it to construct
   * the name of the file that is returned.
   * As the file is now saved with a standard time stamp for both iOS and Android,
   * then iOS specific code is not needed any more.
   * If a time stamp is not part of the file URI then the method assumes this is an
   * existing gallery selected file and just returns the original name of the file.
   *
   * @arg {string} fileUri The fileUri of the file to be attached in the app.
   * @arg {boolean} isEdited Check for if the element has been edited.
   * @return {string} The file name that should be used for this file.
   */
  generateTimeStampFileName(fileUri: string, isEdited = false): string {
    const pathSplitted = fileUri.split('/');
    const ext = pathSplitted[pathSplitted.length - 1].split('?')[0].split('.')[1];
    const timeStamp = pathSplitted[pathSplitted.length - 1].split('?')[0].split('.')[0];

    const fileDate = new Date(Number(timeStamp));

    let fileName;

    const append = isEdited ? '-edit' : '';

    if (!isNaN(fileDate.getDate())) {
      const photosArray = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'heic', 'heif'];
      const filmArray = ['avi', 'mp4', 'mov'];
      let prefix = 'File';
      if (photosArray.includes(ext)) {
        prefix = 'Photo';
      } else if (filmArray.includes(ext)) {
        prefix = 'Video';
      }

      fileName = prefix + ' - ' + fileDate.getDate() + '-' + (fileDate.getMonth() + 1) + '-' + fileDate.getFullYear() + '-' +
      fileDate.getHours() + ':' + (fileDate.getMinutes() < 10 ? '0' : '') + fileDate.getMinutes() + ':' + (fileDate.getSeconds() < 10 ? '0' : '') + fileDate.getSeconds() +  append + '.' + ext;
    } else {
      fileName = timeStamp + append + '.' + ext;
    }

    return fileName;
  }

  /**
   * Adds '-added from gallery' to a file name. Also adds an index if this is a file
   * from the multi image picker.
   *
   * @arg {string} fileName The name of the file to be appended.
   * @arg {number} index The index of a multi selected file in an array.
   * @return {string} The appended file name.
   */
  appendFromGallery(fileName: string, index = -1): string {
    const names = fileName.split('.');
    let newName = '';
    if (index === -1) {
      newName = names[0] + '-added from gallery';
    } else {
      newName = names[0] + '-' + index + '-added from gallery';
    }

    return newName + '.' + names[1];
  }

  /**
   * Adds '-added from media library' to a file name being returned from the media library.
   *
   * @arg {string} fileName The name of the file to be appended.
   * @return {string} The appended file name.
   */
  appendFromMediaLibrary(fileName: string): string {
    const names = fileName.split('.');
    const newName = names[0] + '-added from media library';

    return newName + '.' + names[1];
  }

  /**
   * Finds all the child inspection types that are assigned to the current property where
   * the parent types is not assigned. It updates the name to include the parent type name
   * then adds it to a array of child inspection types to return.
   *
   * @arg {ModuleLevelEntry} topLevelInspectionTypes All the parent type inspections (including the subtypes) assigned to the user.
   * @arg {Property} property The current property.
   * @return {any} An array of child inspection types that do not have their parent types assigned to the current property.
   */
   findAssignedSubTypes(topLevelInspectionTypes: ModuleLevelEntry[], property: Property): any {
    let assignedChildInspectionTypes = [];
    for (const parentInspectionType of topLevelInspectionTypes) {
      if (parentInspectionType.subLevelEntries && parentInspectionType.subLevelEntries.length) {
        for (const childInspectionType of parentInspectionType.subLevelEntries) {
          if (property.inspectionSubtypes.includes(childInspectionType.id) && !property.inspectionTypes.includes(parentInspectionType.id)) {
            const newParentType: ModuleLevelEntry = {
              id: childInspectionType.id,
              auditId: childInspectionType.auditId,
              level: 1,
              name: parentInspectionType.name + ' / ' + childInspectionType.name,
              description: childInspectionType.description,
              subLevelEntries: [],
              applicableToUnits: childInspectionType.applicableToUnits,
              applicableToRegister: childInspectionType.applicableToRegister,
              expectedFrequency: childInspectionType.expectedFrequency };
            assignedChildInspectionTypes = assignedChildInspectionTypes.concat(newParentType);
          }
        }
      }
    }
    return assignedChildInspectionTypes;
  }

  /**
   * This method takes a picture, video or file and saves it to
   * a permanent specified folder for both iOS and Android, either the External Storage or
   * the External directory respectively. This means that a defined file path is no longer
   * needed to access and preview these files, all we need is the storage enum and the
   * filename.
   *
   * @arg {any} originalFile The original file uri returned by the ngx camera plugin
   * that automatically saves it to a temporary folder.
   * @return {Promise<StatResult>} The GetUriResult object of the file saved to a permanent documents folder.
   */
  public async saveFile(originalFile: any): Promise<StatResult> {
    const fileInTempStorage = await Filesystem.readFile({ path: originalFile });

    const date = new Date();
    const time = date.getTime();
    const ext = this.getFileExtension(originalFile);
    const fileName = time + '.' + ext;

    await Filesystem.writeFile({
      data: fileInTempStorage.data,
      path: fileName,
      directory: this.getFilesystemDirectory()
    });

    const finalFileUri = await Filesystem.stat({
      directory: this.getFilesystemDirectory(),
      path: fileName
    });

    return finalFileUri;
  }

  /**
   * Copies and saves the file selected by the user from the media library, that is in
   * temporary storage into the apps permanent storage folder.
   *
   * @arg {any} originalFile The file object returned by the chooser file selector plugin.
   * @return {Promise<StatResult>} The file uri of the permanently saved file.
   */
  public async saveAndroidMediaFile(originalFile: any): Promise<StatResult> {
    const date = new Date();
    const time = date.getTime();
    const ext = this.getAndroidFileExt(originalFile.name);
    const fileName = time + '.' + ext;

    await Filesystem.writeFile({
      data: originalFile.dataURI,
      path: fileName,
      directory: this.getFilesystemDirectory()
    });

    const finalFileUri = await Filesystem.stat({
      directory: this.getFilesystemDirectory(),
      path: fileName
    });

    return finalFileUri;
  }

  /**
   * Gets the extension of the file from the original file name and returns it.
   *
   * @arg {string} fileName The original file name of the selected file.
   * @return {string} The extension of the selected file.
   */
  getAndroidFileExt(fileName: string) {
    const pathSplitted = fileName.split('.');
    const ext = pathSplitted[pathSplitted.length - 1];
    return ext;
  }

  /**
   * Saves a file passed to the method to permanent storage with a time stamp filename
   * and returns the file uri for the new file.
   *
   * @arg {any} originalFile A file returned in an array from the multi image picker.
   * @return {Promise<StatResult>} The uri of the file now saved to permanent storage.
   */
  public async saveGalleryFile(originalFile: any): Promise<StatResult> {
    const fileInTempStorage = await Filesystem.readFile({ path: originalFile.path });

    const date = new Date();
    const time = date.getTime();
    const ext = originalFile.format;
    const fileName = time + '.' + ext;

    await Filesystem.writeFile({
      data: fileInTempStorage.data,
      path: fileName,
      directory: this.getFilesystemDirectory()
    });

    const finalFileUri = await Filesystem.stat({
      directory: this.getFilesystemDirectory(),
      path: fileName
    });

    return finalFileUri;
  }

  /**
   * Deletes files from local storage.
   *
   * @arg {string} file The file uri to be deleted.
   */
  public async deleteFile(file: string) {
    await Filesystem.deleteFile({
      directory: this.getFilesystemDirectory(),
      path: file
    });
  }

  /**
   * Gets the file system directory.
   *
   * @return {string} File system directory.
   */
  getFilesystemDirectory() {
    if (this.platform.is('ios')) {
      return Directory.ExternalStorage;
    } else {
      return Directory.External;
    }
  }

  /**
   * Gets the extension of the file from the file Uri and returns it so that a new filename can be chosen.
   *
   * @arg {string} fileUri The file Uri of the file returned from the ngx camera/gallery plugin.
   * @return {string} The extension of the file as a string.
   */
  getFileExtension(fileUri: string): string {
    const pathSplitted = fileUri.split('/');
    const ext = pathSplitted[pathSplitted.length - 1].split('?')[0].split('.')[1];
    if (ext === undefined && fileUri.includes('https')) {
      return '';
    }
    return ext;
  }

  /**
   * Checks the extension of the file to see if it is an image file or not.
   *
   * @arg {string} fileUri The file URI of the file that needs to be checked.
   * @return {boolean} True is returned if the file is an image, false if not.
   */
  checkIfImageFile(fileUri: string): boolean {
    const photosArray = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'heic', 'heif'];
    const ext = this.getFileExtension(fileUri).toLowerCase();
    if (photosArray.includes(ext)) {
      return true;
    }
    return false;
  }

  /**
   * Checks the extension of the file to see if it is a video file or not.
   *
   * @arg {string} fileUri The file URI of the file that needs to be checked.
   * @return {boolean} True is returned if the file is a video, false if not.
   */
  public checkIfVideoFile(fileUri: string): boolean {
    const videosArray = ['avi', 'mp4', 'mov'];
    const ext = this.getFileExtension(fileUri).toLowerCase();
    if (videosArray.includes(ext)) {
      return true;
    }
    return false;
  }

  /**
   * Displays the modal welcome message when the information button on the smart sync view is pressed.
   */
  public async showWelcomeMessage() {
    const modal = await this.modalController.create({
      component: HtmlModalComponent,
      componentProps: {
        modalElements: translationObject.currentTranslation.PAGES.SMART_SYNC.WELCOME_MESSAGE,
        modalTitle: translationObject.currentTranslation.PAGES.SMART_SYNC.WELCOME_HEADER,
        modalDismiss: translationObject.currentTranslation.PAGES.SMART_SYNC.WELCOME_OK
      },
      id: 'custom-modal'
    });

    modal.present();
  }

  /**
   * Copies the multiple creation action and attaches the new copy to one of the register items in the
   * array of the original action.
   *
   * @arg {NewAction} currentAction The action to be copied and modified.
   * @arg {number} registerItemId The ID of the register Item that this action should be attached to.
   * @arg {number} creationType The type of action creation that should be assigned to the copy.
   * @return {NewAction} The copied modified action is return.
   */
  public createNewSingleRegisterAction(currentAction: NewAction, registerItemId: number, creationType: number): NewAction {
    const registerAction: NewAction = {
      actionCategory: {
        id: currentAction.actionCategory.id,
        name: currentAction.actionCategory.name
      },
      actionSubCategory: {
        id: currentAction.actionSubCategory ? currentAction.actionSubCategory.id : 0
      },
      action_details: currentAction.action_details,
      action_required: currentAction.action_required,
      modificationDate: currentAction.modificationDate,
      isCompleted: currentAction.isCompleted,
      actioned: currentAction.actioned,
      inspector: {
        id: currentAction.inspector.id
      },
      note: currentAction.note,
      priority: {
        id: currentAction.priority.id
      },
      action_date: currentAction.action_date,
      progress: [],
      registerItemId: [registerItemId],
      registerCreationType: creationType,
      auditQuestionId: currentAction.auditQuestionId? currentAction.auditQuestionId : -1
    };

    if (currentAction.actionCategory.name) {
      registerAction.actionCategory.name = currentAction.actionCategory.name;
    }
    if (currentAction.id) {
      registerAction.id = currentAction.id;
    }
    if (currentAction.actionModuleId) {
      registerAction.actionModuleId = currentAction.actionModuleId;
    }
    if (currentAction.moduleId) {
      registerAction.moduleId = currentAction.moduleId;
    }
    if (currentAction.close_type) {
      registerAction.close_type = currentAction.close_type;
    }
    if (currentAction.completionDetails) {
      registerAction.completionDetails = currentAction.completionDetails;
    }
    if (currentAction.photo) {
      registerAction.photo = {
        id: currentAction.photo.id,
        path: currentAction.photo.path
      };
    }
    if (currentAction.pendingUpload) {
      registerAction.pendingUpload = currentAction.pendingUpload;
    }
    if (currentAction.priority.name) {
      registerAction.priority.name = currentAction.priority.name;
    }
    if (currentAction.referenceNo) {
      registerAction.referenceNo = currentAction.referenceNo;
    }
    if (currentAction.propertyId) {
      registerAction.propertyId = currentAction.propertyId;
    }
    if (currentAction.attachments) {
      registerAction.attachments = [];
      for (const attachment of currentAction.attachments) {
        const newAttachment = {
          name: attachment.name,
          path: attachment.path
        };
        registerAction.attachments.push(newAttachment);
      }
    }
    registerAction.completionAttachments = [];
    if (registerAction.actionSubCategory && !registerAction.actionSubCategory.id) {
      delete registerAction.actionSubCategory;
    }

    return registerAction;
  }

  /**
   * Creates a fire door and initialises it from the register template.
   *
   * @arg {number} registerId The ID of the register event, i.e. fire door ID or asbestos ID.
   * @arg {RegisterStructure[]} registerStructure The template of the register item to be added.
   * @arg {NewRegisterRow[]} registerRows All register rows on the current inspection, used to generate a random ID.
   * @return {NewRegisterRow} A newly created and initialised fire door.
   */
  public createNewFireDoor(registerId: number, propertyId: number, registerStructure: RegisterStructure[], registerRows: NewRegisterRow[]): NewRegisterRow {
    const randomId = this.genRandomId(registerRows) * -1;
    // Create new fire door without any fields.
    const newFireDoor: NewRegisterRow = {
      id: randomId,
      registerId,
      isEdited: 1,
      newRegisterFields: [],
      newItem: 1
    };
    // Add each field initialised from the register template.
    for (const field of registerStructure) {
      let dynamicValue;
      switch (field.name) {
        case 'reviewStatus': {
          dynamicValue = field.defaultValue;
          break;
        }
        case 'id': {
          dynamicValue = randomId;
          break;
        }
        case 'propertyId': {
          dynamicValue = propertyId;
          break;
        }
        default : {
          dynamicValue = '';
        }
      }

      const newField: NewRegisterField = {
        keyName: field.name,
        webName: field.webName,
        value: dynamicValue,
        id: field.id,
        fileAttachments: [],
        fieldType: field.fieldType
      };
      newFireDoor.newRegisterFields.push(newField);
    }

    return newFireDoor;
  }

  /**
   * Triggers sending the emitter for the settings dismissed event.
   * When the settings view has been dismissed then the smart sync view will update translations depending on the
   * selections that were made.
   */
  sendSettingsDismissedEmitter() {
    this.settingsDismissedEvent.next(null);
  }

  /**
   * Saves the selected code to the device.
   *
   * @arg {string} codeSelected The code for the language selected by the user.
   */
  saveLanguageSetting(codeSelected: string, showSelector: boolean) {
    const newLanguageSetting: LanguageSelected = {
      code: codeSelected,
      showSelector: showSelector
    };

    this.storageService.set<LanguageSelected>(
      StorageKey.LANGUAGE,
      newLanguageSetting
    );
  }

  /**
   * Checks to see if the selected date is given in the GMT or BST timezones and converts BST to GMT
   * for correct TimeStamp conversion.
   *
   * @review This method is needed for a bug with the current date picker plugin for Android only.
   * Once the date picker is updated to use the Ionic native date picker then this should be reviewed to
   * see if it still relevant.
   *
   * @arg {any} date The selected date.
   * @return {Date} The converted Date object is returned.
   */
  parseSelectedDate(date: any): Date {
    let newDate = null;
    if (date.toString().includes('GMT+0100 (British Summer Time)')) {
        const dateSplitArray = date.toString().split('G');
        newDate = dateSplitArray[0] + 'GMT+0000 (Greenwich Mean Time)';
    } else {
        newDate = date;
    }
    return new Date(newDate);
  }

  /**
   * Sets the colour of the action and status bar (for capacitor 7).
   * 
   * @arg {string} colour The colour to set the action and status bar.
   */
  async setStatusBarColour(colour: string) {
    await EdgeToEdge.setBackgroundColor({ color: colour });
  }

  /**
   * Initialises the device data and then returns it.
   *
   * @return {DeviceData} Returns the initialised device data.
   */
  initDeviceData(dataOnDevice: DeviceData): DeviceData {
      dataOnDevice = new DeviceData();

      this.storageService.set<DeviceData>(
        StorageKey.DATA_ON_DEVICE,
        dataOnDevice
      );
  
      return dataOnDevice;
  }

}
